/**
 * SINGLE SOURCE OF TRUTH for what each plan may do.
 * Every gate in the Workers reads from here. The client may read it too, but the
 * client's answer is never trusted — see src/middleware/requirePlan.ts.
 */

export type PlanId = "free" | "pro";

/** Everything that can be gated. Add a capability here, then map it below. */
export type Capability =
  | "unlimited_spots"
  | "solved_charts"          // anything the pipeline solved — the paid asset
  | "modeled_postflop"       // the retained heuristic model; FREE tier only feature
  | "all_table_sizes"
  | "all_icm_contexts"
  | "bounty_drills"
  | "full_sng_simulator"
  | "spaced_repetition"
  | "custom_opponents"
  /*
   * `range_grid` and `full_coaching` used to sit here, and both are now BUILT AND
   * UNGATED — see the trainer's right rail. They were removed rather than moved to
   * the free tier because a capability is a lock, and neither one can be locked
   * honestly:
   *
   * The chart node route sends the WHOLE 169-hand strategy for any spot the account
   * paid a spot for. It has to — the client deals from those weights — and a
   * deliberate earlier decision keeps `freqs` off the free grading route precisely
   * because the node route is where they legitimately arrive. So the range grid and
   * the derived prose are pictures of numbers already in the client's hands. Gating
   * the picture would be a lock on the door of a room with no walls, and this
   * codebase does not ship locks that a devtools panel walks through.
   *
   * What is actually metered is unchanged and real: spots per day, table sizes, ICM
   * contexts. A free account sees ten full ranges a day and Pro sees as many as it
   * likes, which is a gate on the asset rather than on its rendering.
   */
  | "full_leak_report"
  | "hand_history_import"
  | "weekly_digest"
  | "blackjack_history";      // the sitting-by-sitting record, not the analysis itself

export interface Plan {
  id: PlanId;
  label: string;
  /** Spots per UTC day. null = unlimited. */
  dailySpots: number | null;
  capabilities: ReadonlySet<Capability>;
  /** Table sizes this plan may select. */
  tableSizes: readonly number[];
}

const FREE_CAPS: Capability[] = [
  /**
   * The free tier trains on the SAME solved charts as Pro. It is bounded by two
   * other dials instead: ten spots a day, and six-handed only.
   *
   * It used to be `modeled_postflop` and nothing else, on the plan that free would
   * get the prototype's heuristic postflop trainer. That trainer was never built and
   * every published chart is `source: "solved"` — so `can("free", "solved_charts")`
   * was false for all of them, both chart endpoints returned 402 before the meter was
   * ever reached, and `dailySpots: 10` was dead code. Every free signup would have
   * opened the trainer, pressed the only button, and been told the app was broken.
   *
   * Giving free the real thing is also the better funnel: a limit someone runs into
   * having enjoyed the product sells Pro. An error message does not.
   */
  "solved_charts",
  "modeled_postflop",
];

/**
 * WHAT THE BLACKJACK ROOM COSTS — master plan §9 (11 September 2026) replaced the old
 * rule here, which kept every blackjack number free.
 *
 * The TABLE is free for everybody, signed in or not: deal, play, the verdict on each
 * decision against the chart, the Ledger. The EXACT numbers — every option's expected
 * value and what a choice gave up, in units — plus the Count Drill, Decision Spots and
 * the record across sittings are Blackjack Pro: $9.99 once, or included in Poker Pro.
 *
 * There is still no `blackjack_analysis` CAPABILITY, on purpose. A capability belongs
 * to a plan, and Blackjack Pro is not a plan — it is a one-time unlock (`UNLOCKS`
 * below) that a free account can hold. So the room asks `unlocksFor`, never `can`.
 * `blackjack_history` stays in PRO_CAPS because Poker Pro includes it; the Worker's
 * history route also accepts the unlock (`src/routes/game.ts`).
 *
 * Unlike a spot's frequencies, the room's expected values are computed in the browser
 * from the rules and the shoe — there is nothing the server withholds. The Blackjack Pro
 * lock on those numbers is the room declining to print them, and that is a known limit
 * of this design, recorded in the Phase 9 report.
 */
const PRO_CAPS: Capability[] = [
  "unlimited_spots",
  "solved_charts",
  "modeled_postflop",
  "all_table_sizes",
  "all_icm_contexts",
  "bounty_drills",
  "full_sng_simulator",
  "spaced_repetition",
  "custom_opponents",
  "full_leak_report",
  "hand_history_import",
  "weekly_digest",
  "blackjack_history",
];

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: "free",
    label: "Free",
    dailySpots: 10,
    capabilities: new Set(FREE_CAPS),
    tableSizes: [6],
  },
  pro: {
    id: "pro",
    label: "Pro",
    dailySpots: null,
    capabilities: new Set(PRO_CAPS),
    tableSizes: [2, 3, 4, 5, 6, 7, 8, 9],
  },
};

export const PRICING = {
  monthly: { amountCents: 1899, interval: "month" as const, priceEnvKey: "STRIPE_PRICE_PRO_MONTHLY" },
  annual:  { amountCents: 15900, interval: "year"  as const, priceEnvKey: "STRIPE_PRICE_PRO_ANNUAL" },
  trialDays: 7,
  /**
   * The public founder offer: a locked rate for early subscribers.
   *
   * A real subscription at a discount — a customer, with a card, who can cancel and
   * who counts. Distinct from `GIFT_SEATS` below in every way that matters.
   *
   * The coupon itself is created in the Stripe dashboard and its value is set there;
   * we only reference the code. Until that coupon exists in Stripe, submitting this
   * code yields an ordinary full-price checkout rather than an error — the safe
   * direction to fail, since the alternative is a 500 at the moment of payment.
   */
  founderCoupon: "FOUNDER",
  founderSeats: 50,
} as const;

/**
 * THIS BLOCK USED TO DESCRIBE A PRODUCT THAT DOES NOT EXIST, and it was left here
 * corrected rather than deleted because somebody had already started building from it.
 *
 * It said the founder offer was "a redeemable code that grants a comp entitlement with
 * no expiry, no card and no Stripe object at all", with `product_id = 'founder'` and a
 * revoke button in the admin screen. None of that is true of the code as it stands.
 * `FOUNDER` is a Stripe promotion code applied to an ordinary `pro_monthly` or
 * `pro_annual` subscription — a real customer, with a card, who counts as a customer
 * everywhere we count them — and there is no revoke path.
 *
 * How the seats are actually accounted, in `src/routes/billing.ts`:
 *
 *   - A claim is written to `audit_log` under `founder_coupon_applied` at the moment
 *     the code is accepted, inside the same statement that checks the cap, so
 *     concurrent checkouts cannot overshoot it.
 *   - It is RELEASED if Stripe did not actually apply a discount — which includes the
 *     supported state above, where the promotion code has not been created in the
 *     dashboard yet and the checkout falls through to full price. Without that
 *     release, advertising the code before creating it in Stripe would have destroyed
 *     the whole offer before one founder ever got a discount.
 *   - A claim holds its seat for 48 hours, or indefinitely once that person has a live
 *     Stripe entitlement. An abandoned checkout therefore lapses, which is what stops
 *     fifty throwaway accounts from taking the offer off the table.
 *
 * If the redeemable-comp design above is the one we want, it is a piece of work, not a
 * comment: it needs its own product id, its own redemption endpoint and an admin
 * revoke. Until then this is what the code does.
 */

/**
 * Gift seats: friends and family.
 *
 * Free for life, granted by an admin against a specific person, and deliberately
 * **not redeemable**. There is no code, no public endpoint and nothing on the
 * pricing page — because there is nothing here that should be self-serve. These go
 * to people we know by name, so the right interface is an admin typing that name,
 * not a string that works for whoever ends up holding it.
 *
 * Kept apart from the founder coupon below, which is a discount for real customers.
 * The two are different offers with different failure modes and they should never
 * share a code path:
 *
 *   - A gift is `product_id = 'gift'`. No card, no Stripe object, no expiry, nothing
 *     to cancel or refund, and it counts as zero customers everywhere we measure.
 *   - The founder coupon produces an ordinary `pro_monthly` / `pro_annual`
 *     subscription at a discount. That person IS a customer.
 *
 * The proof of concept is twenty paying subscribers. It is not proved by twenty
 * people who love you.
 */
export const GIFT_SEATS = {
  /** The entitlement product id. Never `pro_monthly` or `pro_annual`. */
  productId: "gift",
  /**
   * A ceiling, not a target. Nothing self-serve can reach this — it exists so that a
   * mistake in the admin screen is bounded, and so the count is visible on it.
   */
  max: 25,
} as const;

/**
 * ======================= PHASE 9: THE TWO ONE-TIME UNLOCKS ========================
 * Master plan §9, signed off by Tim on 11 September 2026, as written:
 *
 *   - Poker Pro stays $18.99/mo · $159/yr, 7-day trial, 50 FOUNDER seats — and it
 *     INCLUDES Blackjack Pro and Cribbage Pro.
 *   - Blackjack Pro, $9.99 once: the exact-EV advisor numbers, the Count Drill, Decision
 *     Spots, and the sitting-by-sitting history.
 *   - Cribbage Pro, $5.99 once: the discard analyzer, the Pegging Drill, rating history.
 *     The room is "Coming", so today it is the entitlement and nothing to spend it on.
 *   - No fourth "all three" SKU. The tables themselves are free in every room.
 *
 * An unlock is NOT a plan. `users.plan` is `free | pro` with a CHECK on it, and this
 * phase makes no schema change: an unlock is an `entitlements` row with its own
 * `product_id` (`src/lib/entitlement.ts`, `ONE_TIME_PRODUCTS`), it never counts toward
 * `refreshUserPlan`, and `unlocksFor` is the one rule for who holds one. The two rules
 * that matter are pinned in `tests/plans.test.ts` (and, against the real SQL, in
 * `tests/phase9-plans.test.ts`): Poker Pro implies both unlocks, and an unlock never
 * implies Poker Pro.
 *
 * Web checkout is Stripe, one-time (`mode: payment`), through `src/routes/billing.ts`;
 * the store listings (Capacitor, later) sell the same two product ids.
 * ================================================================================
 */
export type UnlockId = "blackjack_pro" | "cribbage_pro";

export interface Unlock {
  id: UnlockId;
  label: string;
  /** Whole cents, once. The Pricing page and the site both read this. */
  amountCents: number;
  /** The Worker secret holding the Stripe one-time Price id. */
  priceEnvKey: string;
  room: "blackjack" | "cribbage";
  /** What it buys, in the words the Pricing page prints. */
  grants: readonly string[];
  /** True while the room it unlocks is not open yet. */
  roomComing: boolean;
}

export const UNLOCKS: Record<UnlockId, Unlock> = {
  blackjack_pro: {
    id: "blackjack_pro", label: "Blackjack Pro", amountCents: 999, priceEnvKey: "STRIPE_PRICE_BLACKJACK_PRO",
    room: "blackjack", roomComing: false,
    grants: [
      "The exact expected value of every option, every hand",
      "Count Drill",
      "Decision Spots",
      "Your record across sittings",
    ],
  },
  cribbage_pro: {
    id: "cribbage_pro", label: "Cribbage Pro", amountCents: 599, priceEnvKey: "STRIPE_PRICE_CRIBBAGE_PRO",
    room: "cribbage", roomComing: true,
    grants: ["Discard analyzer", "Pegging Drill", "Rating history"],
  },
};
export const UNLOCK_IDS = Object.keys(UNLOCKS) as UnlockId[];

/** What Poker Pro includes. Both — the plan is the bundle; there is no fourth SKU. */
export const POKER_PRO_INCLUDES: readonly UnlockId[] = ["blackjack_pro", "cribbage_pro"];

/**
 * The unlocks an account holds: everything Poker Pro includes, plus whatever it bought
 * once. `owned` is the account's live one-time product ids; anything else in it is
 * ignored rather than trusted.
 */
export function unlocksFor(plan: PlanId, owned: readonly string[]): UnlockId[] {
  const out = new Set<UnlockId>(plan === "pro" ? POKER_PRO_INCLUDES : []);
  for (const id of owned) if ((UNLOCK_IDS as string[]).includes(id)) out.add(id as UnlockId);
  return UNLOCK_IDS.filter((id) => out.has(id));
}

export function can(plan: PlanId, cap: Capability): boolean {
  return PLANS[plan].capabilities.has(cap);
}
export function allowsTableSize(plan: PlanId, size: number): boolean {
  return PLANS[plan].tableSizes.includes(size);
}
export function dailySpotLimit(plan: PlanId): number | null {
  return PLANS[plan].dailySpots;
}

/**
 * Community and growth links. Values arrive from the owners; blank means the UI
 * hides the element rather than rendering a dead link.
 */
export const COMMUNITY = {
  /** Discord invite. Blank until an owner provides it. */
  discordUrl: "",
  /** Referral links are tracked only — no reward logic at launch. */
  referral: { enabled: true, rewardLogic: false, linkBase: "/r/" },
} as const;
